<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import PerformanceLevelVisualBadge from '@/core/badges/PerformanceLevelVisualBadge.vue';
import {
  COMPACT_PERFORMANCE_LEVEL_CARD_SIZE_PX,
  getCompactPerformanceLevelGroupWidth,
  getToggledPerformanceLevelId,
} from '@/core/performance-visuals/performanceLevelRating.utils';
import { getPerformanceLevelsSortedByLevels } from '@/utils/performance.utils';
import { computed } from 'vue';

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: number | null];
}>();

const props = withDefaults(
  defineProps<{
    appearance?: 'badges' | 'cards';
    accessibleLabel: string;
    density?: 'compact' | 'default';
    disabled?: boolean;
    modelValue: number | null;
    performanceVisual?: Components.Schemas.PerformanceVisualDto | null;
  }>(),
  {
    appearance: 'badges',
    density: 'default',
    disabled: false,
    performanceVisual: undefined,
  },
);

const performanceLevels = computed<Components.Schemas.PerformanceLevelV2Dto[]>(() =>
  getPerformanceLevelsSortedByLevels(props.performanceVisual),
);

const isSelected = (performanceLevel: Components.Schemas.PerformanceLevelV2Dto): boolean => {
  return props.modelValue === performanceLevel.id;
};

const selectPerformanceLevel = (performanceLevelId: number): void => {
  if (props.disabled) return;
  emit('update:modelValue', getToggledPerformanceLevelId(props.modelValue, performanceLevelId));
};

const groupClass = computed<string>(() => {
  if (props.appearance === 'badges') return 'gap-sm';
  return props.density === 'compact' ? 'gap-smd justify-start' : 'w-full gap-smd justify-center';
});

const groupStyle = computed<Record<string, string>>(() => {
  if (props.appearance !== 'cards' || props.density !== 'compact') return {};

  const style: Record<string, string> = {
    maxWidth: '100%',
    width: `${getCompactPerformanceLevelGroupWidth(performanceLevels.value.length)}px`,
  };
  return style;
});

const cardStyle = computed<Record<string, string>>(() => {
  if (props.density === 'compact') {
    const style: Record<string, string> = {
      flexBasis: `${COMPACT_PERFORMANCE_LEVEL_CARD_SIZE_PX}px`,
      maxWidth: `${COMPACT_PERFORMANCE_LEVEL_CARD_SIZE_PX}px`,
      minWidth: '36px',
    };
    return style;
  }

  const style: Record<string, string> = {
    maxWidth: '100px',
    minWidth: '36px',
  };
  return style;
});

const getOptionClasses = (performanceLevel: Components.Schemas.PerformanceLevelV2Dto): string[] => {
  const selected = isSelected(performanceLevel);

  if (props.appearance === 'cards') {
    const classes = [
      'relative min-w-0 flex-1 basis-0 self-center overflow-hidden rounded-md bg-surface-primary',
      'border transition-[background-color] duration-300',
    ];
    classes.push(
      props.disabled
        ? 'cursor-not-allowed opacity-40'
        : 'cursor-pointer hover:bg-surface-tertiary active:bg-primary/10',
    );
    classes.push(
      selected
        ? 'z-1 border-transparent outline outline-3 outline-tint outline-offset-0 focus:!outline-3 focus:!outline-offset-0'
        : 'border-divider outline-none outline-inner',
    );
    return classes;
  }

  const classes = ['appearance-none rounded-sm border-0 bg-transparent p-0 outline-none'];
  classes.push(props.disabled ? 'cursor-not-allowed' : 'cursor-pointer');
  classes.push(selected ? 'focus:outline-none' : 'outline-inner');
  return classes;
};
</script>

<template>
  <div
    v-bind="$attrs"
    :class="['flex items-center', groupClass]"
    :style="groupStyle"
    role="radiogroup"
    :aria-label="accessibleLabel"
  >
    <button
      v-for="performanceLevel in performanceLevels"
      :key="performanceLevel.id"
      type="button"
      :disabled="disabled"
      :class="getOptionClasses(performanceLevel)"
      :style="appearance === 'cards' ? cardStyle : undefined"
      role="radio"
      :aria-label="performanceLevel.level.toString()"
      :aria-checked="isSelected(performanceLevel)"
      @click.stop="selectPerformanceLevel(performanceLevel.id)"
    >
      <template v-if="appearance === 'cards'">
        <span class="block h-0 pb-[100%]" aria-hidden="true" />
        <span class="absolute inset-0 flex items-center justify-center p-sm">
          <PerformanceLevelVisualBadge
            :performance-level="performanceLevel"
            :class="[
              'size-full !rounded-checkbox !border-0 !bg-transparent text-heading font-semibold text-tint',
              modelValue !== null && !isSelected(performanceLevel) && 'opacity-20 grayscale',
              isSelected(performanceLevel) && '!opacity-100',
            ]"
          />
        </span>
      </template>

      <PerformanceLevelVisualBadge
        v-else
        :performance-level="performanceLevel"
        :selected="isSelected(performanceLevel)"
        :class="[
          'relative size-icon-xl shrink-0 text-heading font-semibold text-tint transition-[background-color,opacity] duration-300',
          disabled ? 'opacity-40' : 'hover:!bg-primary/20 active:!bg-primary/20',
          modelValue !== null && !isSelected(performanceLevel) ? 'opacity-50' : '!opacity-100',
        ]"
      />
    </button>
  </div>
</template>
